import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PartyPopper } from 'lucide-react';
import type { Recommendation } from '@lizrd/contracts';
import { getRecommendations } from '../../lib/api';
import { Card } from '../ui/card';
import { InlineMarkdown } from '../ui/markdown-lite';
import { SkeletonRows } from '../ui/skeleton';
import { Spinner } from '../ui/spinner';
import { EFFORT_META, usd } from '../../lib/graph-visuals';

/** Statuses that still represent actionable, un-acted work (the queue). */
const ACTIONABLE = new Set(['open', 'accepted', 'snoozed']);

/** Biggest estimated saving first. */
const bySaving = (a: Recommendation, b: Recommendation) =>
  b.estimatedMonthlySaving - a.estimatedMonthlySaving;

/**
 * The domain work queue (DASH-002 at env scope): the biggest open savings
 * opportunities, each showing savings and effort, deep-linking straight to that
 * recommendation's details page (which has a "back to the board" link). This is
 * what makes the domain dashboard action-oriented rather than a report.
 */
export function TopOpportunities({ domainId, limit = 5 }: { domainId: string; limit?: number }) {
  const recs = useQuery({
    queryKey: ['recommendations', domainId],
    queryFn: () => getRecommendations(domainId),
    // Poll fast while the first pass is still generating recommendations (e.g.
    // straight out of onboarding), then settle to the idle cadence.
    refetchInterval: (query) => (query.state.data?.computing ? 4_000 : 60_000),
  });
  const computing = recs.data?.computing ?? false;

  const { top, openCount } = useMemo(() => {
    const items = recs.data?.items ?? [];
    const open = items.filter((r) => ACTIONABLE.has(r.status) && r.estimatedMonthlySaving > 0);
    return { top: open.slice().sort(bySaving).slice(0, limit), openCount: open.length };
  }, [recs.data, limit]);

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline p-4">
        <div>
          <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
            Top opportunities
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            Your 5 biggest savings opportunities, ready to act on.
          </p>
        </div>
        {openCount > 0 && (
          <Link
            to="../cost-optimization"
            className="text-xs font-semibold text-iris hover:text-iris-strong"
          >
            View all {openCount} in Optimizations →
          </Link>
        )}
      </div>

      {recs.isLoading ? (
        <SkeletonRows rows={4} rightBlocks={2} />
      ) : computing && top.length === 0 ? (
        <p className="flex items-center gap-2 p-5 text-sm text-muted">
          <Spinner size="sm" />
          Generating your recommendations… they’ll appear here as soon as they’re ready.
        </p>
      ) : top.length === 0 ? (
        <p className="p-5 text-sm text-muted">
          No open opportunities right now — everything actionable has been handled.{' '}
          <PartyPopper aria-hidden className="inline h-4 w-4 align-text-bottom" />
        </p>
      ) : (
        <ul className="divide-y divide-hairline">
          {top.map((r) => {
            const effort = EFFORT_META[r.effortLevel];
            return (
              <li key={r.id}>
                <Link
                  to={`/domains/${domainId}/recommendations/${r.id}`}
                  className="flex flex-wrap items-center gap-x-6 gap-y-2 p-4 transition-colors hover:bg-surface-2/40"
                >
                  <div className="min-w-[220px] flex-1">
                    <span className="truncate font-medium text-bone">
                      <InlineMarkdown text={r.title} />
                    </span>
                    <p className="mt-0.5 truncate text-xs text-muted">
                      {r.resourceName} · {r.resourceType} · {r.provider}
                    </p>
                  </div>
                  <div className="flex items-start gap-6">
                    {/* Fixed-width, single-alignment columns so every row's header and
                        value line up across rows; items-start + a shared h-5 value box
                        keep the two headers (and the two values) on one line within a
                        row despite their different font sizes. */}
                    <div className="w-24 text-right">
                      <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted">
                        Saves / mo
                      </div>
                      <div className="mt-0.5 flex h-5 items-center justify-end text-sm font-medium text-iris tnum">
                        {usd(r.estimatedMonthlySaving)}
                      </div>
                    </div>
                    <div className="w-36 text-left">
                      <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted">
                        Effort
                      </div>
                      <div className="mt-0.5 flex h-5 items-center whitespace-nowrap text-xs text-haze">
                        {effort ? `${effort.label}` : r.effortLevel}
                        {effort && <span className="text-muted"> · {effort.band}</span>}
                      </div>
                    </div>
                    <span className="self-center text-muted" aria-hidden>
                      →
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
